import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Delete,
  Close,
  Search,
} from '@mui/icons-material';
import httpClient from '../../api/axios';

const ContactMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get('/api/contact');
      // Check for proper response structure
      if (response.data && Array.isArray(response.data.data)) {
        setContacts(response.data.data); // Assuming data is in response.data.data
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to fetch contacts',
        'error'
      );
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleView = (contact) => {
    setSelectedContact(contact);
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await httpClient.delete(`/api/contact/${contactToDelete._id}`); // Changed to _id
      setContacts(
        contacts.filter((c) => c._id !== contactToDelete._id)
      ); // Changed to _id
      showSnackbar('Contact deleted successfully');
    } catch (error) {
      console.error('Error deleting contact:', error);
      showSnackbar('Failed to delete contact', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const filteredContacts =
    contacts?.filter(
      (contact) =>
        contact?.name
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        contact?.email
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        contact?.subject
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        contact?.message
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())
    ) || [];

  const visibleColumns = isMobile
    ? ['name', 'email', 'actions']
    : ['name', 'email', 'subject', 'message', 'actions'];

  return (
    <div className='flex justify-center items-start min-h-screen bg-gray-100 p-4 w-full'>
      <Paper className='w-full max-w-7xl p-2 md:p-6 shadow-lg'>
        <Typography
          variant='h5'
          className='text-center font-bold mb-4'
        >
          Contact Messages
        </Typography>

        {/* Search Box */}
        <Box display='flex' gap={2} mb={3}>
          <TextField
            label='Search Contacts'
            variant='outlined'
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <Search />
                </IconButton>
              ),
            }}
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>

        <TableContainer>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                {visibleColumns.includes('name') && (
                  <TableCell>
                    <strong>Name</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('email') && (
                  <TableCell>
                    <strong>Email</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('subject') && (
                  <TableCell>
                    <strong>Subject</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('message') && (
                  <TableCell>
                    <strong>Message</strong>
                  </TableCell>
                )}
                {visibleColumns.includes('actions') && (
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  {/* <TableCell colSpan={visibleColumns.length} align='center'>
                    Loading...
                  </TableCell> */}
                </TableRow>
              ) : filteredContacts.length > 0 ? (
                filteredContacts
                  .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  .map((contact) => (
                    <TableRow key={contact._id}>
                      {visibleColumns.includes('name') && (
                        <TableCell>{contact.name}</TableCell>
                      )}
                      {visibleColumns.includes('email') && (
                        <TableCell>{contact.email}</TableCell>
                      )}
                      {visibleColumns.includes('subject') && (
                        <TableCell>{contact.subject}</TableCell>
                      )}
                      {visibleColumns.includes('message') && (
                        <TableCell>
                          {isMobile
                            ? `${contact.message.substring(0, 20)}...`
                            : contact.message.length > 50
                            ? `${contact.message.substring(0, 50)}...`
                            : contact.message}
                        </TableCell>
                      )}
                      {visibleColumns.includes('actions') && (
                        <TableCell>
                          <Box display='flex' gap={1}>
                            <IconButton
                              onClick={() => handleView(contact)}
                              size='small'
                            >
                              <Visibility fontSize='small' />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                handleDeleteClick(contact)
                              }
                              size='small'
                              color='error'
                            >
                              <Delete fontSize='small' />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    align='center'
                  >
                    No contacts found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={filteredContacts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Paper>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Contact Details</DialogTitle>
        <DialogContent dividers>
          {selectedContact && (
            <Box sx={{ p: 2 }}>
              <Typography variant='subtitle1' gutterBottom>
                <strong>Name:</strong> {selectedContact.name}
              </Typography>
              <Typography variant='subtitle1' gutterBottom>
                <strong>Email:</strong> {selectedContact.email}
              </Typography>
              <Typography variant='subtitle1' gutterBottom>
                <strong>Subject:</strong> {selectedContact.subject}
              </Typography>
              <Typography
                variant='subtitle1'
                sx={{ whiteSpace: 'pre-line' }}
              >
                <strong>Message:</strong> {selectedContact.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewDialogOpen(false)}
            color='primary'
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this contact message?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color='error'
            variant='contained'
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ContactMaster;
